import clsx from "clsx";
import React, { useContext, useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams, Link as RouterLink, Prompt, useHistory, useLocation } from "react-router-dom";

import { Grid, Typography, IconButton, Divider, Hidden, Button, Tooltip } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import InfoIcon from "@material-ui/icons/Info";
import CloseIcon from "@material-ui/icons/Close";

import GeofencesMap from "./GeofencesMap";
import StartingMenuPanel from "./StartingMenuPanel";
import LayersListPanel from "./LayersListPanel";
import EditLayerPanel from "./EditLayerPanel";
import EditZonePanel from "./EditZonePanel";
import { FleetContext, GeofencesContext } from "../../context";
import { ErrorVisibilityDuration } from "../../constants";
import { SectionGridLayout, SectionGridContainer } from "../../components/Layout";
import { getBoundingBox } from "../../services/map.service";
import { setGeofencesSavedData } from "../../services/geofences.service";
import { useDialog } from "../../hooks/useDialog";

const padding = 10;
const useStyles = makeStyles((theme) => ({
  section: {
    // full height - margins: want to fill the screen
    height: `calc(100vh - ${theme.spacing(6)}px)`,
    flexDirection: "column",
    flexWrap: "nowrap",
  },
  container: { position: "relative" },
  fixedContainer: {
    position: "fixed",
    zIndex: 999,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    padding: theme.spacing(2),
    margin: 0,
    boxSizing: "border-box",
    background: theme.palette.background.paper,
  },
  closeButton: {
    position: "fixed",
    zIndex: 999,
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
    margin: 0,
  },
  mapContainer: {
    position: "relative",
    height: "100%",
  },
  panelsContainer: {
    position: "absolute",
    zIndex: 999,
    top: 0,
    left: theme.spacing(2),
    width: `calc(100% - ${theme.spacing(4)}px)`,
    height: `calc(100% - ${theme.spacing(2)}px)`,
    pointerEvents: "none",
  },
  fixedPanels: {
    top: theme.spacing(2),
    height: `calc(100% - ${theme.spacing(4)}px)`,
  },
  panelsGrid: {
    display: "grid",
    gap: `${theme.spacing(2)}px`,
    gridTemplateColumns: "repeat(4, 1fr)",
    width: `calc(100% - ${padding * 2}px)`,
    maxHeight: "100%",
    padding,
    pointerEvents: "none",
  },
  mobileNotice: {
    position: "absolute",
    zIndex: 999,
    top: "50%",
    left: 0,
    right: 0,
    padding: theme.spacing(2),
    margin: theme.spacing(0, 2, 0, 2),
    background: theme.palette.background.paper,
    boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
    transform: "translateY(-50%)",
    textAlign: "center",
  },
  vertDivider: { borderRight: `1px solid ${theme.palette.primary.divider}` },
  noPad: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  marginRight: { marginRight: theme.spacing(1) },
  actionButtons: { marginLeft: "auto" },
  error: {
    position: "absolute",
    zIndex: 999,
    bottom: theme.spacing(2),
    left: theme.spacing(2),
    right: theme.spacing(2),
  },
  errorIcon: {
    marginLeft: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  tooltip: {
    fontSize: "0.75em",
  },
}));

const Geofences = ({ isEditMode }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { activeFleet, setActiveFleet } = useContext(FleetContext);
  const { fleetId } = useParams();
  const {
    key,
    name,
    layers,
    selectedLayer,
    selectedZone,
    error,
    clearError,
    clearGeofencesData,
    fetchFleetGeofences,
  } = useContext(GeofencesContext);

  const hasNotPersistedZones =
    layers &&
    Object.values(layers).some((layer) =>
      Object.values(layer.zones).some((zone) => !zone.persisted),
    );

  const noLayers = !layers || !Object.keys(layers).length;
  const layersMissingZones =
    layers && Object.values(layers).some((layer) => !Object.keys(layer.zones).length);

  const unpersistedZones =
    layers &&
    !Object.values(layers).every(
      (layer) =>
        Object.keys(layer.zones).length > 0 &&
        Object.values(layer.zones).every((zone) => zone.persisted),
    );

  const exportDisabled = noLayers || layersMissingZones || unpersistedZones;

  const getExportDisabledTooltip = () => {
    if (noLayers) {
      return t("Fleet.Geofences.Buttons.ExportTooltip.NoLayers");
    }
    if (layersMissingZones) {
      return t("Fleet.Geofences.Buttons.ExportTooltip.LayersMissingZones");
    }
    return t("Fleet.Geofences.Buttons.ExportTooltip.UnpersistedZones");
  };

  const [isSaving, setIsSaving] = useState(true);
  const [forceRedirectPath, setForceRedirectPath] = useState("");
  const [fullScreenMode, setFullScreenMode] = useState(false);

  const [openBlockDialog, blockDialog] = useDialog({
    body: t("Fleet.Geofences.Dialogs.BlockBody"),
  });

  useEffect(() => {
    setActiveFleet(fleetId);
  }, [fleetId]);

  useEffect(() => {
    if (key && layers) {
      try {
        setGeofencesSavedData({ key, fleetId, name: name.value, layers });
        if (!isSaving) setIsSaving(true);
      } catch (_) {
        if (isSaving) setIsSaving(false);
      }
    } else if (!isSaving) {
      setIsSaving(true);
    }
  }, [name.value, layers]);

  useEffect(() => {
    if (error) {
      setTimeout(clearError, ErrorVisibilityDuration);
    }
  }, [error]);

  useEffect(() => {
    if (forceRedirectPath) {
      history.push(forceRedirectPath);
    }
  }, [forceRedirectPath]);

  useEffect(() => {
    window.addEventListener("keydown", exitFullScreenMode);

    return () => {
      window.removeEventListener("keydown", exitFullScreenMode);
    };
  });

  useEffect(() => {
    if (isEditMode) {
      (async () => {
        await fetchFleetGeofences(fleetId);
      })();
    }
  }, []);

  const openExportModal = () => {
    history.push({
      pathname: `${location.pathname}/export`,
      state: { background: location, key, name, layers },
    });
  };

  const onBlockNavigation = (givenLocation) => {
    if (
      forceRedirectPath ||
      givenLocation.state?.background ||
      givenLocation.pathname === location.pathname
    ) {
      setForceRedirectPath("");
      return true;
    }

    if (hasNotPersistedZones) {
      openBlockDialog(() => () => {
        setForceRedirectPath(givenLocation.pathname);
      });
      return false;
    }
    return true;
  };

  const onStartNew = () => {
    history.push(history.location.pathname.slice(0, history.location.pathname.lastIndexOf("/")));
    const callback = () => clearGeofencesData();

    if (hasNotPersistedZones) {
      openBlockDialog(() => callback);
    } else {
      callback();
    }
  };

  const exitFullScreenMode = useCallback((event) => {
    if (!fullScreenMode) return;

    const { keyCode } = event;

    if (keyCode === 27) {
      // Escape
      setFullScreenMode(false);
    }
  });

  const toggleFullScreenMode = () => {
    setFullScreenMode(!fullScreenMode);
  };

  return (
    <SectionGridLayout>
      <SectionGridContainer spaceContents className={classes.section}>
        <Grid item container alignItems="center">
          <IconButton
            className={classes.marginRight}
            component={RouterLink}
            to={`/fleets/${fleetId}/settings`}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" className={classes.marginRight}>
            {`${activeFleet?.name} ${t("Fleet.Geofences.Title")}`}
          </Typography>
          <Grid className={classes.actionButtons}>
            {layers && (
              <Button
                color="primary"
                variant="contained"
                className={classes.marginRight}
                onClick={onStartNew}
              >
                {t("Fleet.Geofences.Buttons.StartNew")}
              </Button>
            )}
            {layers && (
              <Tooltip
                placement="bottom"
                title={getExportDisabledTooltip()}
                disableHoverListener={!exportDisabled}
              >
                <span>
                  <Button
                    color="primary"
                    variant="contained"
                    className={classes.marginRight}
                    onClick={openExportModal}
                    disabled={exportDisabled}
                  >
                    {t("Fleet.Geofences.Buttons.Export")}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Button color="primary" variant="contained" onClick={toggleFullScreenMode}>
              {t("Fleet.Geofences.Buttons.FullScreenMode")}
            </Button>
          </Grid>
        </Grid>
        <Grid item className={classes.noPad}>
          <Divider />
        </Grid>
        <Grid
          item
          xs={12}
          className={clsx(classes.container, fullScreenMode && classes.fixedContainer)}
        >
          <Grid item xs={12} className={classes.mapContainer}>
            <GeofencesMap
              defaultBounds={getBoundingBox(activeFleet.bounding_box)}
              fullScreenMode={fullScreenMode}
            />
          </Grid>
          <Hidden smDown>
            <Grid className={clsx(classes.panelsContainer, fullScreenMode && classes.fixedPanels)}>
              <Grid className={classes.panelsGrid}>
                {!isEditMode && !layers && <StartingMenuPanel fleetId={fleetId} />}
                {layers && <LayersListPanel />}
                {selectedLayer && selectedLayer.key && <EditLayerPanel />}
                {selectedZone && selectedZone.key && <EditZonePanel />}
              </Grid>
            </Grid>
          </Hidden>
          <Hidden mdUp>
            <Grid item xs={12} className={classes.mobileNotice}>
              {t("Fleet.Geofences.MobileHiddenNotice")}
            </Grid>
          </Hidden>
          {error && (
            <Alert severity="error" className={classes.error}>
              {error}
            </Alert>
          )}
          {!isSaving && (
            <Tooltip
              className={classes.error}
              classes={{ tooltip: classes.tooltip }}
              placement="right"
              title={t("Fleet.Geofences.LocalStorageError")}
            >
              <InfoIcon color="secondary" className={classes.errorIcon} />
            </Tooltip>
          )}
        </Grid>
      </SectionGridContainer>

      {fullScreenMode && (
        <IconButton className={classes.closeButton} onClick={toggleFullScreenMode}>
          <CloseIcon />
        </IconButton>
      )}

      <Prompt when message={onBlockNavigation} />
      {blockDialog}
    </SectionGridLayout>
  );
};

Geofences.propTypes = {
  isEditMode: PropTypes.bool,
};

Geofences.defaultProps = {
  isEditMode: false,
};

export default Geofences;
